import React, { useState } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Link } from "react-router-dom";
import { X } from "lucide-react";
import Logo from "../../assets/images/logo/hazer-logo.png";
import { toast, Toaster } from "react-hot-toast";
import apiUrls from "../../config/api-urls";

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email) {
      setError("Please enter your email");
      return;
    }
    
    setIsLoading(true);
    setError("");

    try {
      const response = await fetch(apiUrls.auth.forgotPassword, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      
      const data = await response.json();
      
      if (!response.ok || data?.error) {
        const backendError =
        data?.error?.message || data?.message || "Failed to send reset email";
      throw new Error(backendError);
      }
      const successMessage =
      data?.data?.message || "Password reset link sent successfully";
      toast.success(successMessage);
      setEmail(""); 
    } catch (err) {
      const message = err.message || "Failed to send reset email. Please try again.";
      setError(message);
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Toaster position="top-right" reverseOrder={false} />
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="w-full max-w-4xl bg-white rounded-2xl shadow-xl overflow-hidden">
          <div className="flex flex-col lg:flex-row min-h-[600px]">
            {/* Left Panel */}
            <div className="hidden sm:flex sm:w-1/2 flex-col items-center justify-center text-center p-6 bg-gradient-to-br from-[#ff9906] to-[#f79d2d] text-white">
              <h2 className="text-2xl md:text-3xl font-bold mb-2">
                Welcome to Hazer Globals
              </h2>
              <p className="text-sm md:text-lg">
                We make it easy for everyone to access their account.
              </p>
              <div className="bg-white rounded-2xl mt-4">
                <img
                  src={Logo || "/placeholder.svg"}
                  alt="Building"
                  className="mt-6 w-32 h-32 md:w-40 md:h-40 max-w-xs rounded-lg shadow-lg"
                />
              </div>
            </div>

            {/* Right Panel - Forgot Password Form */}
            <div className="lg:w-1/2 p-8 lg:p-12 flex flex-col justify-center">
              <div className="w-full max-w-md mx-auto">
                <div className="flex justify-between items-center mb-8">
                  <h2 className="text-2xl font-bold text-gray-900">
                    Forgot Password
                  </h2>
                  <Link to="/login">
                    <X className="w-6 h-6 text-gray-400 hover:text-gray-600 cursor-pointer" />
                  </Link>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="space-y-2">
                    <Label
                      htmlFor="email"
                      className="text-sm font-medium text-gray-700"
                    >
                      Email Address
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        if (error) setError(""); // Clear error on input change
                      }}
                      placeholder="Enter your email address"
                      required
                      className="w-full px-4 py-3 bg-gray-100 border-0 rounded-lg focus:bg-white focus:ring-2 focus:ring-[#ff8c00] focus:ring-opacity-50"
                    />
                  </div>

                  {error && (
                    <div className="text-sm text-red-600 bg-red-50 p-3 rounded-lg">
                      {error}
                    </div>
                  )}

                  <p className="text-sm text-gray-600">
                    Enter your email address and we'll send you a link to reset
                    your password.
                  </p>

                  <Button
                    type="submit"
                    disabled={isLoading || !email}
                    className="w-full bg-[#ff8c00] hover:bg-[#e67c00] text-white py-3 rounded-lg font-medium disabled:opacity-50"
                  >
                    {isLoading ? "Sending..." : "Send Reset Link"}
                  </Button>

                  <div className="flex justify-center">
                    <Link to="/login">
                      <Button
                        variant="outline"
                        className="px-8 py-2 rounded-lg bg-transparent"
                      >
                        Back to Login
                      </Button>
                    </Link>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}